"use client";

import { createContext, type ReactNode, useContext } from "react";
import type { ChartSourceHost, ChartSourceResolver } from "./chart-source-types";
import { ChartFetchBudgetProvider } from "./chartFetchBudget";

const ChartSourceContext = createContext<ChartSourceResolver | null>(null);
const ChartSourceHostContext = createContext<ChartSourceHost | null>(null);

export interface ChartSourceProviderProps {
  /**
   * Turns a serializable source descriptor into a fetcher. Keep it stable
   * (`useCallback`) — a new identity on every render re-resolves every chart
   * beneath it.
   */
  resolve?: ChartSourceResolver;
  /**
   * The component form of `resolve`, for sources whose resolution needs hooks
   * (see `ChartSourceHost`). Consulted FIRST when both are installed, because a
   * host is the more capable half — an app registering both means the host
   * knows refs the plain resolver cannot answer, and a host that does not
   * recognise a ref falls through to `resolve` below.
   */
  host?: ChartSourceHost;
  /**
   * Ceiling on tile fetches in flight across every chart below this provider.
   *
   * Omit it for the default. It exists as an override mainly so a test can set
   * a ceiling small enough to observe — the product has no reason to vary it.
   */
  maxConcurrentFetches?: number;
  children: ReactNode;
}

/**
 * Supplies the runtime half of streaming chart sources.
 *
 * ── Why a provider and not a prop ─────────────────────────────────────────────
 * The fetcher is a function, so it cannot ride in a block's props — those are
 * serialized into `data-composition-schema`, and a function there blanks the
 * page. But the chart still needs one. A provider resolves that: the block
 * declares WHICH data it wants in JSON, and the app answers HOW to get it once,
 * above the whole tree, instead of at every call site.
 *
 * This is the same division the maps blocks already use, where a block declares
 * a tileset in props and the host attaches tile authentication at the map
 * itself — *"render-time concerns owned by the runtime, not props on this union."*
 *
 * ── Why it lives in `edges` ───────────────────────────────────────────────────
 * So the charts can stay ignorant of where data comes from. `edges` has no
 * GraphQL client and must not grow one; the app installs a resolver that closes
 * over its own. That keeps the dependency pointing one way — the app knows
 * about charts, charts never know about the app.
 *
 * Mount it once, near the top:
 *
 * ```tsx
 * <ChartSourceProvider resolve={dashboardChartSources}>
 *   <App />
 * </ChartSourceProvider>
 * ```
 */
export function ChartSourceProvider({ resolve, host, maxConcurrentFetches, children }: ChartSourceProviderProps) {
  return (
    <ChartSourceContext.Provider value={resolve ?? null}>
      <ChartSourceHostContext.Provider value={host ?? null}>
        {/*
         * The fetch ceiling shares this provider's scope deliberately: anything
         * that can RESOLVE a streaming source is something whose fetches the
         * budget must count. Mounting it here means an app gets the guard by
         * installing the provider it already installs, with nothing to wire.
         */}
        <ChartFetchBudgetProvider
          {...(maxConcurrentFetches !== undefined ? { maxConcurrent: maxConcurrentFetches } : {})}
        >
          {children}
        </ChartFetchBudgetProvider>
      </ChartSourceHostContext.Provider>
    </ChartSourceContext.Provider>
  );
}

/**
 * The installed resolver, or `null` when no provider is mounted.
 *
 * `null` is a legitimate state, not an error: a chart with a streaming source
 * rendered outside a provider shows its empty state. That is what makes a
 * component library safe to render in a test, a docs page, or a screenshot
 * harness with no data layer attached — and it is why this returns rather than
 * throws.
 */
export function useChartSourceResolver(): ChartSourceResolver | null {
  return useContext(ChartSourceContext);
}

/**
 * The installed host, or `null` when none is mounted.
 *
 * Separate hook rather than a merged return so a chart can tell the two apart:
 * a host has to be RENDERED to answer, and picking that path is a decision about
 * component structure, not a value to read.
 */
export function useChartSourceHost(): ChartSourceHost | null {
  return useContext(ChartSourceHostContext);
}
